// src/components/Shared/LoginPage.jsx
// Login screen aligned to Figma auth layout

import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { IconEye, IconEyeOff } from './FigmaIcons';
import BackButton from './BackButton';

const LoginPage = () => {
	const { login } = useContext(AuthContext);
	const navigate = useNavigate();
	const location = useLocation();

	const [emailOrUsername, setEmailOrUsername] = useState('');
	const [password, setPassword] = useState('');
	const [rememberMe, setRememberMe] = useState(false);
	const [showPassword, setShowPassword] = useState(false);
	const [errorMsg, setErrorMsg] = useState('');
	const [loading, setLoading] = useState(false);

	const handleSubmit = async (e) => {
		e.preventDefault();
		// Password managers and browser autofill can write into the fields
		// without firing React's onChange (most reliably when they fill before
		// hydration on a slow connection). State then lags what the user can
		// see, and a state-only submit posts empty fields from a filled-looking
		// form: the browser's `required` check passes because the DOM has text,
		// then the server answers "Please enter all fields". The DOM is the
		// truth at submit time, so read the values from the form itself.
		const fields = e.currentTarget.elements;
		const domValue = (fieldName, fallback) => {
			const el = fields.namedItem(fieldName);
			return typeof el?.value === 'string' ? el.value : fallback;
		};
		const identity = domValue('emailOrUsername', emailOrUsername).trim();
		const pass = domValue('password', password);
		if (identity !== emailOrUsername) setEmailOrUsername(identity);
		if (pass !== password) setPassword(pass);
		setErrorMsg('');
		setLoading(true);

		try {
			await login(identity, pass);
			if (rememberMe) {
				localStorage.setItem('eventory.rememberAuth', '1');
			} else {
				localStorage.removeItem('eventory.rememberAuth');
			}
			const from = location.state?.from;
			const fromPath = from?.pathname || '';
			const isNonEventAdminPath = fromPath.startsWith('/admin') && !fromPath.startsWith('/admin/event/');
			const redirectPath = isNonEventAdminPath
				? '/dashboard'
				: `${fromPath}${from?.search || ''}${from?.hash || ''}` || '/dashboard';
			navigate(redirectPath, { replace: true });
		} catch (error) {
			setErrorMsg(error || 'Invalid credentials. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="ev-page ev-auth-page">
			<div className="ev-auth-card">
				<div className="ev-back-row">
					<BackButton to="/" label="Back to home" />
				</div>
				<div className="ev-auth-head">
					<div className="ev-auth-logo">
						<img src="/images/eventory-logo.svg" alt="Eventory" />
					</div>
					<h1 className="ev-auth-title">Welcome back</h1>
					<p className="ev-auth-subtitle">Sign in to keep running your events.</p>
				</div>

				{errorMsg && <div className="ev-alert ev-alert-danger">{errorMsg}</div>}

				<form onSubmit={handleSubmit} className="ev-auth-grid">
					<div className="ev-auth-field">
						<label htmlFor="emailOrUsername">Email Address</label>
						<div className="ev-auth-input-wrap">
							<input
								type="text"
								id="emailOrUsername"
								name="emailOrUsername"
								autoComplete="username"
								className="ev-auth-input"
								value={emailOrUsername}
								onChange={(e) => setEmailOrUsername(e.target.value)}
								placeholder="e.g., you@example.com"
								required
							/>
						</div>
					</div>

					<div className="ev-auth-field">
						<label htmlFor="password">Password</label>
						<div className="ev-auth-input-wrap">
							<input
								type={showPassword ? 'text' : 'password'}
								id="password"
								name="password"
								autoComplete="current-password"
								className="ev-auth-input"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder="Your password"
								required
							/>
							<button
								type="button"
								className="ev-auth-eye"
								onClick={() => setShowPassword((prev) => !prev)}
								aria-label={showPassword ? 'Hide password' : 'Show password'}
							>
								{showPassword ? <IconEyeOff className="ev-icon-svg" /> : <IconEye className="ev-icon-svg" />}
							</button>
						</div>
					</div>

					<div className="ev-check-row" style={{ justifyContent: 'space-between' }}>
						<label className="ev-check" htmlFor="rememberMe">
							<input
								type="checkbox"
								id="rememberMe"
								checked={rememberMe}
								onChange={(e) => setRememberMe(e.target.checked)}
							/>
							<span>Remember me</span>
						</label>
						<Link to="/forgot-password" className="ev-inline-link">Forgot password?</Link>
					</div>

					<button type="submit" className="ev-auth-primary" disabled={loading}>
						{loading ? 'Signing in…' : 'Log in'}
					</button>

					<p className="ev-auth-switch">
						Don’t have an account yet? <Link to="/register" className="ev-inline-link">Sign up</Link>
					</p>
				</form>
			</div>
		</div>
	);
};

export default LoginPage;
